import { useRef } from 'react';
import './SegmentedControl.css';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  /** One of ariaLabel / ariaLabelledby should be provided for accessibility. */
  ariaLabel?: string;
  ariaLabelledby?: string;
  className?: string;
}

/**
 * Themed segmented control — a small set of mutually-exclusive options shown
 * inline (an on-brand replacement for a native `<select>` when there are only
 * a few choices). Exposed as an ARIA radiogroup with roving tabindex and
 * arrow-key navigation.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  ariaLabelledby,
  className,
}: Readonly<Props<T>>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, dir: 1 | -1) => {
    const n = options.length;
    const to = (from + dir + n) % n;
    onChange(options[to].value);
    buttons.current[to]?.focus();
  };
  const extraClass = className ? ` ${className}` : '';

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={`seg-control${disabled ? ' seg-control--disabled' : ''}${extraClass}`}
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            ref={(el) => {
              buttons.current[i] = el;
            }}
            className={`seg-control-item${active ? ' seg-control-item--on' : ''}`}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(i, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(i, -1);
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
